import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChannelAutomation,
  channelAutomationService,
  ChannelAutomationPayload,
  ChannelAutomationQueryParams,
  ChannelAutomationRuleTestRequest,
} from '@/services/channelAutomationService';
import { PaginatedResponse } from '@/services/apiClient';
import { tvChannelKeys } from '@/hooks/useTVChannel';

export const channelAutomationKeys = {
  all: ['channelAutomations'] as const,
  lists: () => [...channelAutomationKeys.all, 'list'] as const,
  list: (params: ChannelAutomationQueryParams) => [...channelAutomationKeys.lists(), params] as const,
  runs: (id: string) => [...channelAutomationKeys.all, 'runs', id] as const,
};

export const useChannelAutomationsQuery = (params: ChannelAutomationQueryParams = {}) => {
  const queryResult = useQuery({
    queryKey: channelAutomationKeys.list(params),
    queryFn: () => channelAutomationService.getAutomations(params),
  });

  return {
    data: queryResult.data,
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: queryResult.error,
    refetch: queryResult.refetch,
  };
};

export const useChannelAutomations = () => {
  const params: ChannelAutomationQueryParams = { _page: 0, _size: 2000, _sort: '+name' };

  return useQuery({
    queryKey: channelAutomationKeys.list(params),
    queryFn: () => channelAutomationService.getAutomations(params),
    select: (data) => data.content,
  });
};

export const useChannelAutomationRuns = (id: string | undefined) => {
  return useQuery({
    queryKey: channelAutomationKeys.runs(id ?? 'unknown'),
    queryFn: () => {
      if (!id) {
        throw new Error('Automatización no encontrada');
      }
      return channelAutomationService.getRuns(id);
    },
    enabled: Boolean(id),
    select: (data) => data.runs,
  });
};

export const useCreateChannelAutomation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: ChannelAutomationPayload) => channelAutomationService.createAutomation(request),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: channelAutomationKeys.all }),
  });
};

export const useTestChannelAutomationRule = () => {
  return useMutation({
    mutationFn: (request: ChannelAutomationRuleTestRequest) => channelAutomationService.testRule(request),
  });
};

export const useUpdateChannelAutomation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ChannelAutomationPayload }) =>
      channelAutomationService.updateAutomation(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: channelAutomationKeys.all }),
  });
};

const toAutomationPayload = (automation: ChannelAutomation, enabled: boolean): ChannelAutomationPayload => ({
  name: automation.name,
  enabled,
  iptvProviderId: automation.iptvProviderId,
  flussonicServerId: automation.flussonicServerId,
  cronExpression: automation.cronExpression,
  timezone: automation.timezone,
  notes: automation.notes ?? undefined,
  rules: automation.rules.map((rule) => ({
    id: rule.id,
    displayOrder: rule.displayOrder,
    enabled: rule.enabled,
    matchRegex: rule.matchRegex,
    playlistGroup: rule.playlistGroup ?? null,
    tvChannelId: rule.tvChannelId,
    flussonicStreamName: rule.flussonicStreamName,
    channelTitleTemplate: rule.channelTitleTemplate ?? null,
    appendMatchSuffix: rule.appendMatchSuffix,
    notes: rule.notes ?? null,
  })),
});

export const useToggleChannelAutomationStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ automation, enabled }: { automation: ChannelAutomation; enabled: boolean }) =>
      channelAutomationService.updateAutomation(automation.id, toAutomationPayload(automation, enabled)),
    onSuccess: (data) => {
      queryClient.setQueriesData<PaginatedResponse<ChannelAutomation>>(
        { queryKey: channelAutomationKeys.lists() },
        (current) => current
          ? {
              ...current,
              content: current.content.map((automation) =>
                automation.id === data.automation.id ? data.automation : automation
              ),
            }
          : current
      );
      queryClient.invalidateQueries({ queryKey: channelAutomationKeys.all });
    },
  });
};

export const useDeleteChannelAutomation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => channelAutomationService.deleteAutomation(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: channelAutomationKeys.all }),
  });
};

export const useRunChannelAutomation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => channelAutomationService.runAutomation(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: channelAutomationKeys.all });
      queryClient.invalidateQueries({ queryKey: channelAutomationKeys.runs(id) });
      queryClient.invalidateQueries({ queryKey: tvChannelKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['tvChannels', 'all'] });
    },
  });
};
